// src/pages/admin/UserMasterManager.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  FormControl,
  InputGroup,
  Modal,
  Row,
  Spinner,
  Table,
  Pagination,
} from "react-bootstrap";
import { toast } from "react-toastify";
import {
  Edit2,
  Plus,
  RefreshCcw,
  Search,
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
} from "react-feather";

import "../../../css/master-common.css";

import {
  fetchUsersThunk,
  createUserThunk,
  updateUserThunk,
  deleteUserThunk,
  openUserModal,
  closeUserModal,
  setUserForm,
  resetUserForm,
  setUserFilters,
} from "../../../store/slices/userMasterSlice";

const ITEMS_PER_PAGE = 10;

const Pill = ({ children }) => <Badge className="mm-pill">{children}</Badge>;

const StatusPill = ({ status }) => {
  const st = String(status ?? "").toLowerCase();
  const isActive = st === "active" || Number(status) === 1;
  return <Pill>{isActive ? "Active" : "Inactive"}</Pill>;
};

const RolePill = ({ role }) => <Pill>{String(role || "-")}</Pill>;

export default function UserMasterManager() {
  const dispatch = useDispatch();

  const { rows, loading, saving, deleting, error, modalOpen, modalMode, form, filters } =
    useSelector((s) => s.userMaster);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchUsersThunk());
  }, [dispatch]);

  const filteredRows = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    let list = Array.isArray(rows) ? rows : [];

    if (filters?.role) {
      list = list.filter(
        (r) => String(r.role || "").toLowerCase() === String(filters.role).toLowerCase()
      );
    }

    if (filters?.status !== "" && filters?.status != null) {
      const fs = String(filters.status).toLowerCase();
      list = list.filter((r) => {
        const st = String(r.status ?? "").toLowerCase();
        const active = st === "active" || Number(r.status) === 1;
        const inactive = st === "inactive" || Number(r.status) === 0;

        if (fs === "active" || fs === "1") return active;
        if (fs === "inactive" || fs === "0") return inactive;
        return String(r.status) === String(filters.status);
      });
    }

    if (!q) return list;

    return list.filter((r) =>
      `${r.uid ?? ""} ${r.username ?? ""} ${r.role ?? ""} ${r.status ?? ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [rows, search, filters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, filters?.role, filters?.status]);

  const totalPages = Math.ceil(filteredRows.length / ITEMS_PER_PAGE) || 1;
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedRows = filteredRows.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const getPaginationItems = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const isMobile = typeof window !== "undefined" && window.innerWidth < 576;
    const maxVisiblePages = isMobile ? 3 : 5;

    const goTo = (p) => setCurrentPage(Math.min(Math.max(1, p), totalPages));

    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => goTo(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <ChevronLeft size={16} className="me-1" />
        Prev
      </Pagination.Prev>
    );

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => goTo(1)}>
          1
        </Pagination.Item>
      );
      if (!isMobile && startPage > 2) items.push(<Pagination.Ellipsis key="ellipsis-start" />);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => goTo(i)}>
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (!isMobile && endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis-end" />);
      }
      items.push(
        <Pagination.Item key={totalPages} onClick={() => goTo(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => goTo(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        Next
        <ChevronRight size={16} className="ms-1" />
      </Pagination.Next>
    );

    return items;
  };

  const openAdd = () => {
    dispatch(resetUserForm());
    dispatch(setUserForm({ role: "user", status: "active" }));
    dispatch(openUserModal({ mode: "add" }));
  };

  const openEdit = (r) => {
    dispatch(
      setUserForm({
        uid: r.uid,
        username: r.username,
        password: "",
        role: r.role || "user",
        status:
          String(r.status || "").toLowerCase() === "active"
            ? "active"
            : String(r.status || "").toLowerCase() === "inactive"
            ? "inactive"
            : Number(r.status) === 1
            ? 1
            : 0,
      })
    );
    dispatch(openUserModal({ mode: "edit" }));
  };

  const onSave = async () => {
    const payload = {
      uid: form.uid,
      username: String(form.username || "").trim(),
      password: String(form.password || "").trim(),
      role: String(form.role || "user").trim(),
      status:
        String(form.status).toLowerCase() === "active"
          ? "active"
          : String(form.status).toLowerCase() === "inactive"
          ? "inactive"
          : Number(form.status) === 1
          ? 1
          : 0,
    };

    if (!payload.username || !payload.role) {
      toast.warning("Please fill all required fields");
      return;
    }

    if (modalMode === "add" && !payload.password) {
      toast.warning("Password is required for new user");
      return;
    }

    const res =
      modalMode === "add"
        ? await dispatch(createUserThunk(payload))
        : await dispatch(updateUserThunk(payload));

    if (res?.meta?.requestStatus === "fulfilled") {
      toast.success(modalMode === "add" ? "User created ✅" : "User updated ✅");
      dispatch(closeUserModal());
      dispatch(fetchUsersThunk());
    } else {
      toast.error(res?.payload || "Failed to save user");
    }
  };

  const onDelete = (r) => {
    toast.info(
      ({ closeToast }) => (
        <div>
          <div className="fw-semibold mb-1">Delete user "{r.username}"?</div>

          <div className="d-flex gap-2 mt-2">
            <button
              className="btn btn-sm btn-danger"
              onClick={async () => {
                const res = await dispatch(deleteUserThunk(r.uid));
                if (res?.meta?.requestStatus === "fulfilled") {
                  toast.success("User deleted ✅");
                  dispatch(fetchUsersThunk());
                } else {
                  toast.error(res?.payload || "Failed to delete user");
                }
                closeToast();
              }}
            >
              Delete
            </button>

            <button className="btn btn-sm btn-secondary" onClick={closeToast}>
              Cancel
            </button>
          </div>
        </div>
      ),
      { autoClose: false }
    );
  };

  const clearSearch = () => setSearch("");

  return (
    <div className="container-fluid px-3 py-4 mm-page">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1 fw-bold mm-title">User Master</h4>
          {/* <div className="mm-subtitle">Create / Edit / Delete Users</div> */}
        </div>

        <div className="d-flex gap-2">
          <Button
            variant="light"
            className="mm-cancel-btn d-flex align-items-center gap-2"
            onClick={() => dispatch(fetchUsersThunk())}
            disabled={loading}
            title="Refresh"
          >
            <RefreshCcw size={16} />
            {loading ? "Refreshing..." : "Refresh"}
          </Button>

          <Button className="mm-primary-btn d-flex align-items-center gap-2" onClick={openAdd}>
            <Plus size={18} />
            Add User
          </Button>
        </div>
      </div>

      {error ? (
        <Alert variant="danger" className="mm-card-soft mb-3">
          <div className="fw-bold mb-1">Error</div>
          <div>{String(error)}</div>
        </Alert>
      ) : null}

      <Card className="mm-card-soft mb-3">
        <Card.Body className="p-3">
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <Form.Label className="fw-bold mb-1">Role</Form.Label>
              <Form.Select
                className="mm-select"
                value={filters.role || ""}
                onChange={(e) => dispatch(setUserFilters({ role: e.target.value }))}
              >
                <option value="">All</option>
                <option value="superadmin">superadmin</option>
                <option value="user">user</option>
              </Form.Select>
            </Col>

            <Col md={3}>
              <Form.Label className="fw-bold mb-1">Status</Form.Label>
              <Form.Select
                className="mm-select"
                value={filters.status ?? ""}
                onChange={(e) =>
                  dispatch(setUserFilters({ status: e.target.value === "" ? "" : e.target.value }))
                }
              >
                <option value="">All</option>
                <option value="active">active</option>
                <option value="inactive">inactive</option>
                <option value="1">1</option>
                <option value="0">0</option>
              </Form.Select>
            </Col>

            <Col md={6}>
              <Form.Label className="fw-bold mb-1">Search</Form.Label>
              <InputGroup>
                <InputGroup.Text className="mm-inputgroup-text">
                  <Search size={18} className="text-muted" />
                </InputGroup.Text>

                <FormControl
                  className="mm-input mm-inputgroup-control"
                  placeholder="Search UID / username / role..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />

                {search ? (
                  <Button variant="light" className="mm-clear-btn" onClick={clearSearch}>
                    <X size={16} />
                  </Button>
                ) : null}
              </InputGroup>
            </Col>

            <Col md={12} className="text-end">
              <div className="text-muted small">
                Showing{" "}
                <span className="fw-bold">
                  {filteredRows.length === 0 ? 0 : startIndex + 1}-
                  {Math.min(startIndex + ITEMS_PER_PAGE, filteredRows.length)}
                </span>{" "}
                of <span className="fw-bold">{filteredRows.length}</span> users
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mm-card">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <div className="mt-2 text-muted">Loading users...</div>
            </div>
          ) : (
            <>
              <div className="mm-table-wrap">
                <Table hover className="mm-table mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 110 }}>UID</th>
                      <th style={{ width: 320 }}>Username</th>
                      <th style={{ width: 200 }}>Role</th>
                      <th style={{ width: 160 }}>Status</th>
                      <th className="mm-actions-th" style={{ width: 180, textAlign: "right" }}>
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedRows.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="p-5 text-center">
                          <div>
                            <div className="mm-empty-icon mx-auto mb-2">
                              <Search size={22} />
                            </div>
                            <div className="fw-bold mb-1">
                              {search || filters.role || filters.status !== ""
                                ? "No results found"
                                : "No users found"}
                            </div>
                            <div className="text-muted">
                              {search || filters.role || filters.status !== ""
                                ? "No users match your filters."
                                : "Get started by adding your first user."}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedRows.map((r) => (
                        <tr key={r.uid}>
                          <td className="text-muted fw-semibold">#{r.uid}</td>
                          <td className="fw-bold" title={r.username}>
                            {r.username}
                          </td>
                          <td>
                            <RolePill role={r.role} />
                          </td>
                          <td>
                            <StatusPill status={r.status} />
                          </td>
                          <td className="mm-actions-td" style={{ textAlign: "right" }}>
                            <div className="d-inline-flex gap-2">
                              <Button
                                variant="light"
                                className="mm-icon-btn primary"
                                onClick={() => openEdit(r)}
                                disabled={saving}
                                title="Edit"
                              >
                                <Edit2 size={16} />
                              </Button>

                              <Button
                                variant="light"
                                className="mm-icon-btn danger"
                                onClick={() => onDelete(r)}
                                disabled={deleting}
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {totalPages > 1 ? (
                <div className="d-flex justify-content-center py-3 border-top mm-footer">
                  <Pagination className="mb-0 master-pagination">{getPaginationItems()}</Pagination>
                </div>
              ) : null}
            </>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={modalOpen}
        onHide={() => dispatch(closeUserModal())}
        centered
        className="mm-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">
            {modalMode === "add" ? "Add User" : "Edit User"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row className="g-3">
            <Col md={12}>
              <Form.Label className="fw-bold">Username / Email *</Form.Label>
              <Form.Control
                className="mm-input"
                value={form.username || ""}
                onChange={(e) => dispatch(setUserForm({ username: e.target.value }))}
                placeholder="Ex: admin@domain.com"
                autoFocus
              />
            </Col>

            <Col md={12}>
              <Form.Label className="fw-bold">
                Password {modalMode === "add" ? "*" : "(optional)"}
              </Form.Label>
              <Form.Control
                className="mm-input"
                type="password"
                value={form.password || ""}
                onChange={(e) => dispatch(setUserForm({ password: e.target.value }))}
                placeholder={modalMode === "add" ? "Enter password" : "Leave blank to keep same"}
              />
            </Col>

            <Col md={6}>
              <Form.Label className="fw-bold">Role *</Form.Label>
              <Form.Select
                className="mm-select"
                value={form.role || "user"}
                onChange={(e) => dispatch(setUserForm({ role: e.target.value }))}
              >
                <option value="superadmin">superadmin</option>
                <option value="user">user</option>
              </Form.Select>
            </Col>

            <Col md={6}>
              <Form.Label className="fw-bold">Status</Form.Label>
              <Form.Select
                className="mm-select"
                value={String(form.status || "active")}
                onChange={(e) => dispatch(setUserForm({ status: e.target.value }))}
              >
                <option value="active">active</option>
                <option value="inactive">inactive</option>
              </Form.Select>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="outline-secondary"
            className="mm-cancel-btn"
            onClick={() => dispatch(closeUserModal())}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            className="mm-primary-btn"
            onClick={onSave}
            disabled={
              saving ||
              !String(form.username || "").trim() ||
              !String(form.role || "").trim() ||
              (modalMode === "add" && !String(form.password || "").trim())
            }
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}